/**
 * useCallNotifications Hook (V1104: Polling Architecture)
 *
 * React Query + SSE 기반 실시간 통화 알림 관리.
 * SSE로 CALL_NOTIFICATION_UPDATE 이벤트 수신 시 자동 refetch.
 */

import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  callNotificationApi,
  type CallNotificationDto,
} from "@/api/call-notification";
import { useEventSource } from "@/hooks/useEventSource";
import { useAuthStore } from "@/stores/authStore";

const QUERY_KEY = "call-notifications";
const UNREAD_COUNT_KEY = "call-notifications-unread-count";

interface UseCallNotificationsOptions {
  organizationId: number | undefined;
  enabled?: boolean;
}

export function useCallNotifications({
  organizationId,
  enabled = true,
}: UseCallNotificationsOptions) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isEnabled = enabled && isAuthenticated && !!organizationId;

  // 알림 목록 조회 (최근 20건)
  const notificationsQuery = useQuery({
    queryKey: [QUERY_KEY, organizationId],
    queryFn: () =>
      callNotificationApi.getNotifications(organizationId!, { size: 20 }),
    enabled: isEnabled,
    refetchInterval: 30000, // 30초마다 폴백 refetch
    staleTime: 5000,
  });

  // 미읽음 개수 조회
  const unreadCountQuery = useQuery({
    queryKey: [UNREAD_COUNT_KEY, organizationId],
    queryFn: () => callNotificationApi.getUnreadCount(organizationId!),
    enabled: isEnabled,
    refetchInterval: 15000,
    staleTime: 5000,
  });

  // SSE 이벤트 수신 시 자동 refetch
  const handleSseMessage = useCallback(
    (message: { type?: string }) => {
      if (message.type === "CALL_NOTIFICATION_UPDATE") {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, organizationId],
        });
        queryClient.invalidateQueries({
          queryKey: [UNREAD_COUNT_KEY, organizationId],
        });
        // 상담 목록도 무효화
        queryClient.invalidateQueries({ queryKey: ["consults"] });
      }
    },
    [queryClient, organizationId],
  );

  useEventSource("notification", handleSseMessage, {
    enabled: isEnabled,
  });

  // 단건 읽음 처리 (V1224+: userId는 서버의 JWT에서 추출, 클라이언트 전달 불필요)
  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => callNotificationApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, organizationId] });
      queryClient.invalidateQueries({
        queryKey: [UNREAD_COUNT_KEY, organizationId],
      });
    },
  });

  // 전체 읽음 처리 (V1224+: userId 제거)
  const markAllAsReadMutation = useMutation({
    mutationFn: () => callNotificationApi.markAllAsRead(organizationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, organizationId] });
      queryClient.invalidateQueries({
        queryKey: [UNREAD_COUNT_KEY, organizationId],
      });
    },
  });

  const notifications: CallNotificationDto[] =
    notificationsQuery.data?.content ?? [];
  const unreadCount = unreadCountQuery.data?.count ?? 0;

  return {
    notifications,
    unreadCount,
    isLoading: notificationsQuery.isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
  };
}
